export const TimePointSpacingPlugin = {
  id: "timePointSpacing",
  beforeDatasetsDraw(chart, _args, pluginOptions) {
    if (!pluginOptions?.enabled) {
      return;
    }

    const xScale = chart?.scales?.x;
    const chartArea = chart?.chartArea;
    if (!xScale || !chartArea) {
      return;
    }

    const buckets = collectDayBuckets(chart, xScale);
    if (buckets.length < 2) {
      return;
    }

    const layout = computeSpacedDayBucketPositions(buckets, {
      minGapPx: Number(pluginOptions?.minGapPx) || 0,
      gutterPx: Number(pluginOptions?.gutterPx) || 0,
      leftBoundary: chartArea.left,
      rightBoundary: chartArea.right,
    });

    const adjustedXByDayStart = new Map(
      layout.map((bucket) => [bucket.dayStart, bucket.adjustedX]),
    );

    chart.data.datasets?.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta?.data?.length) {
        return;
      }

      meta.data.forEach((element, index) => {
        const rawPoint = dataset?.data?.[index];
        const dayStart = toUtcDayStart(getPointTimeValue(rawPoint));
        const adjustedX = adjustedXByDayStart.get(dayStart);

        if (!element || !Number.isFinite(adjustedX)) {
          return;
        }

        element.x = adjustedX;
      });
    });
  },
};

export function computeSpacedDayBucketPositions(
  buckets,
  {
    minGapPx = 0,
    gutterPx = 0,
    leftBoundary = Number.NEGATIVE_INFINITY,
    rightBoundary = Number.POSITIVE_INFINITY,
  } = {},
) {
  const sortedBuckets = Array.isArray(buckets)
    ? buckets
        .map((bucket) => ({
          dayStart: Number(bucket?.dayStart),
          baseX: Number(bucket?.baseX),
          width: Math.max(0, Number(bucket?.width) || 0),
        }))
        .filter(
          (bucket) =>
            Number.isFinite(bucket.dayStart) && Number.isFinite(bucket.baseX),
        )
        .sort((a, b) => a.dayStart - b.dayStart)
    : [];

  if (sortedBuckets.length < 2) {
    return sortedBuckets.map((bucket) => ({
      ...bucket,
      adjustedX: bucket.baseX,
    }));
  }

  const requiredGapFromPrev = sortedBuckets.map((bucket, index) => {
    if (index === 0) {
      return 0;
    }

    const previous = sortedBuckets[index - 1];
    return Math.max(
      Number(minGapPx) || 0,
      (previous.width + bucket.width) / 2 + (Number(gutterPx) || 0),
    );
  });

  const adjustedXs = sortedBuckets.map((bucket) => bucket.baseX);

  let start = 0;
  while (start < sortedBuckets.length) {
    let end = start;

    while (
      end + 1 < sortedBuckets.length &&
      sortedBuckets[end + 1].baseX - sortedBuckets[end].baseX <
        requiredGapFromPrev[end + 1]
    ) {
      end += 1;
    }

    if (end === start) {
      start += 1;
      continue;
    }

    const cluster = sortedBuckets.slice(start, end + 1);
    const totalSpan = requiredGapFromPrev
      .slice(start + 1, end + 1)
      .reduce((sum, gap) => sum + gap, 0);
    const averageBaseX =
      cluster.reduce((sum, bucket) => sum + bucket.baseX, 0) / cluster.length;

    const clusterAdjustedXs = [averageBaseX - totalSpan / 2];
    for (let i = 1; i < cluster.length; i += 1) {
      clusterAdjustedXs[i] =
        clusterAdjustedXs[i - 1] + requiredGapFromPrev[start + i];
    }

    const minAllowed =
      start === 0
        ? leftBoundary + cluster[0].width / 2
        : adjustedXs[start - 1] + requiredGapFromPrev[start];
    const maxAllowed =
      end === sortedBuckets.length - 1
        ? rightBoundary - cluster[cluster.length - 1].width / 2
        : sortedBuckets[end + 1].baseX - requiredGapFromPrev[end + 1];

    const availableSpan = maxAllowed - minAllowed;
    if (Number.isFinite(availableSpan) && availableSpan >= totalSpan) {
      if (clusterAdjustedXs[0] < minAllowed) {
        const shift = minAllowed - clusterAdjustedXs[0];
        for (let i = 0; i < clusterAdjustedXs.length; i += 1) {
          clusterAdjustedXs[i] += shift;
        }
      }

      const clusterEndX = clusterAdjustedXs[clusterAdjustedXs.length - 1];
      if (clusterEndX > maxAllowed) {
        const shift = maxAllowed - clusterEndX;
        for (let i = 0; i < clusterAdjustedXs.length; i += 1) {
          clusterAdjustedXs[i] += shift;
        }
      }
    }

    for (let i = 0; i < clusterAdjustedXs.length; i += 1) {
      adjustedXs[start + i] = clusterAdjustedXs[i];
    }

    start = end + 1;
  }

  return sortedBuckets.map((bucket, index) => ({
    ...bucket,
    adjustedX: adjustedXs[index],
  }));
}

function collectDayBuckets(chart, xScale) {
  const buckets = new Map();

  chart.data.datasets?.forEach((dataset) => {
    dataset?.data?.forEach((rawPoint, index) => {
      const y = getPointYValue(rawPoint);
      if (!Number.isFinite(y)) {
        return;
      }

      const timeValue = getPointTimeValue(rawPoint);
      const dayStart = toUtcDayStart(timeValue);
      if (!Number.isFinite(dayStart)) {
        return;
      }

      const baseX = xScale.getPixelForValue(dayStart);
      if (!Number.isFinite(baseX)) {
        return;
      }

      const existing = buckets.get(dayStart);
      const width = getPointWidth(dataset, index);
      if (!existing) {
        buckets.set(dayStart, {
          dayStart,
          baseX,
          width,
        });
        return;
      }

      existing.width = Math.max(existing.width, width);
    });
  });

  return Array.from(buckets.values());
}

function getPointTimeValue(rawPoint) {
  if (!rawPoint || typeof rawPoint !== "object") {
    return null;
  }

  const actualX = Number(rawPoint.actualX);
  if (Number.isFinite(actualX)) {
    return actualX;
  }

  const x = Number(rawPoint.x);
  return Number.isFinite(x) ? x : null;
}

function getPointYValue(rawPoint) {
  if (!rawPoint || typeof rawPoint !== "object") {
    return Number(rawPoint);
  }

  const y = Number(rawPoint.y);
  return Number.isFinite(y) ? y : null;
}

function getPointWidth(dataset, index) {
  const radius = resolvePointRadius(dataset?.pointRadius, index);
  if (!Number.isFinite(radius) || radius <= 0) {
    return 0;
  }

  return radius * 2;
}

function resolvePointRadius(pointRadius, index) {
  if (Array.isArray(pointRadius)) {
    const value = Number(pointRadius[index]);
    return Number.isFinite(value) ? value : null;
  }

  const numeric = Number(pointRadius);
  return Number.isFinite(numeric) ? numeric : null;
}

function toUtcDayStart(value) {
  const ms = Number(value);
  if (!Number.isFinite(ms)) {
    return null;
  }

  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}
